/**
 * useCancelOffPageBrief — `POST /v1/off-page-briefs/:opbId/cancel`.
 *
 * Cancels an in-flight workflow (planning/critiquing/grounding). The API is
 * idempotent for terminal-state briefs (already completed/failed/cancelled
 * return 200 with a `note` field, not an error), so callers do not need to
 * guard the click against races.
 *
 * On success invalidates the detail cache so the workspace transitions from
 * the in-progress card to the failed/cancelled view within one refetch.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';

interface CancelResponse {
  status: string;
  note?: string;
}

export function useCancelOffPageBrief(opbId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<CancelResponse>(`/v1/off-page-briefs/${opbId}/cancel`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'detail', opbId],
      });
      void qc.invalidateQueries({
        queryKey: ['off-page-briefs', 'list'],
      });
    },
  });
}
